import { FC, useMemo, } from 'react';
import { Col, Row } from 'antd';
import ScoreSummary from './components/ScoreSummary';
import ScoreSummaryHorizontal from './components/ScoreSummaryHorizontal';
import ScoreCard from './components/ScoreCard';
import FiveDimensional from '@/common/components/business/SiteMap/FiveDimensional';
import ImageLine from './components/ImageLine';
import { isArray, isNotEmpty } from '@lhb/func';
import { markTypeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import cs from 'classnames';
import FourNoSumCon from './components/FourNoSumCon';
import ScoreExplaimIcon from '@/common/components/business/AreaScoreWithoutSum/ScoreExplaimIcon';
import PredictSale from './components/PredictSale';

/**
 * @Description 评分1
 */
const Evaluation: FC<{
  /** 是否商场类型（资源库商场类型标识） */
  resourceMallFlag?: boolean,
  overView: any,
  picList: any[],
  configSwitch: any,
  shareParams?: any,
  isPdf?: boolean,
  modelConfigId?: string,
  isSemirModel?: boolean,
  detail?: any;
  noCheckExtraScore?: boolean;
}> = ({
  resourceMallFlag = false,
  overView,
  picList,
  configSwitch,
  shareParams = {},
  isPdf = false,
  modelConfigId,
  isSemirModel = false,
  detail,
  noCheckExtraScore = false, // 四维有评分状态下，是否不拉取评分说明,点位测评的街边店设置true
}) => {

  // 展示商圈图片：pdf不展示，商场类型不展示
  const showPicList = !isPdf && !resourceMallFlag && picList?.length;
  const showScoreDescriptionFlag = useMemo(() => {
    // 1：默认值，2：自定义富文本，3：不展示
    // 如果没有这个参数，也就是没设置过，就沿用老逻辑展示默认值。
    if (!isNotEmpty(detail.showScoreDescriptionFlag)) {
      return 1;
    } else {
      return detail.showScoreDescriptionFlag;
    }
  }, [detail.showScoreDescriptionFlag]);
  const showTotalScore = useMemo(() => configSwitch?.showTotalScore, [configSwitch]);
  const cardView = useMemo(() => {
    const { scores } = overView || {};
    if (isArray(scores) && scores.length) {
      if (isSemirModel) { // 森马
        return scores.map((scoreItem: any, index: number) => {
          const target: any = {
            ...(scoreItem || {}),
          };
          const { score } = scoreItem || {};
          if (index === 0 || index === 3) {
            target.percent = score / 20 * 100;
          } else {
            target.percent = score / 30 * 100;
          }
          return target;
        });
      }
      return scores?.map((scoreItem: any) => ({
        ...scoreItem,
        percent: scoreItem?.score
      }));
    }
    return [];
  }, [overView, isSemirModel]);

  const isFiveDimen = useMemo(() => {
    return markTypeEnum.five === overView?.markType;
  }, [overView]);

  const isSixDimen = useMemo(() => {
    return markTypeEnum.six === overView?.markType;
  }, [overView]);

  const predictSale = useMemo(() => {
    const {
      salesAmountPredict, // 预测营业额
      lowSalesAmountPredict,
      upSalesAmountPredict,
    } = overView || {};

    // 优先展示区间，无区间值时展示具体值
    const canShowRange = isNotEmpty(lowSalesAmountPredict) && isNotEmpty(upSalesAmountPredict);
    const canShowValue = isNotEmpty(salesAmountPredict);

    return canShowRange || canShowValue;
  }, [overView]);

  /**
   * @description 四维展示
   */
  const renderFourDimen = () => {
    return (<div className={styles.fourDimenCon}>
      <ScoreSummaryHorizontal
        overviewData={overView}
        predictSale={predictSale}
        configSwitch={configSwitch}
        shareParams={shareParams}
        isPdf={isPdf}
        modelConfigId={modelConfigId}
        isSemirModel={isSemirModel}
        noCheckExtraScore={noCheckExtraScore}
        detail={detail}
      />
      <div className={styles.allScoreCon}>
        <Row gutter={[24, 8]}>
          {cardView.length ? cardView.map((item, index: number) => (
            <Col span={12} key={item?.name}>
              <ScoreCard
                title={item?.name}
                score={item?.score}
                totalScore={item?.percent}
                // describe={item?.msg}
                lineColor='#5B9FFF'
                // 森马模型的维度中有四个字/五个字的区别， 为了展示美观
                hasBlank={isSemirModel && (index === 1 || index === 3)}
                isSemirModel={isSemirModel}
              />
            </Col>
          )) : <></>}
        </Row>
      </div>
    </div>);
  };

  /**
   * @description 四维无总分展示
   */
  const renderFourNoSumDimen = () => {
    return (
      <FourNoSumCon
        overViewData={overView}
        predictSale={predictSale}
        cardView={cardView}
        isPdf={isPdf}
        modelConfigId={modelConfigId}
        shareParams={shareParams}
        isSemirModel={isSemirModel}
        showScoreDescriptionFlag={showScoreDescriptionFlag}
        scoreDescription={detail.scoreDescription}
      />
    );
  };

  /**
   * @description 五维/六维展示
   */
  const renderFiveSixDimen = () => {
    return (
      <div className={styles.fiveSixeDimenCon}>
        <div className={styles.halfWidth}>
          <ScoreSummary
            overviewData={overView}
            predictSale={predictSale}
            configSwitch={configSwitch}
            shareParams={shareParams}
            isPdf={isPdf}
            isSemirModel={isSemirModel}
            detail={detail}
          />
        </div>
        <div className={styles.halfWidth}>
          <div className={cs(styles.fiveDimensionChartWrapper, isFiveDimen && styles.fiveDimen, isSixDimen && styles.sixDimen)}>
            <FiveDimensional
              indicator={overView?.scores?.map((item) => ({
                ...item,
                value: item?.score || 0,
                max: 100
              }))}
              seriesDataValue={overView?.scores?.map((item) => item.score || 0)}
              height={isFiveDimen ? 174 : 180}
              radarRadius={isFiveDimen ? '51%' : '50%'}
              valueFontSize={12}
              backgroundColor='#ffffff'
              animation={!isPdf}
              resizable={!isPdf}
            />
          </div>
        </div>
      </div>
    );
  };

  /**
   * @description 五维/六维无总分展示
   */
  const renderFiveSixNoSumDimen = () => {
    // `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_turnover@3x.png`
    return (
      <div className={styles.fiveSixNoSumCon}>
        <Row gutter={[12, 0]}>
          <Col span={predictSale ? 16 : 24}>
            <div className={styles.fiveSixNoSumChartWrapper}>
              <div className={styles.scoreTitle}>
                {isFiveDimen ? '五' : '六'}维评分
                {!isPdf && !isSemirModel && showScoreDescriptionFlag !== 3 && <ScoreExplaimIcon
                  data={overView}
                  predictSale={predictSale}
                  className={styles.titleIcon}
                  showScoreDescriptionFlag={showScoreDescriptionFlag}
                  scoreDescription={detail.scoreDescription}
                />}
              </div>
              <FiveDimensional
                indicator={overView?.scores?.map((item) => ({
                  ...item,
                  value: item?.score || 0,
                  max: 100
                }))}
                seriesDataValue={overView?.scores?.map((item) => item.score || 0)}
                height={180}
                radarRadius={isFiveDimen ? '51%' : '50%'}
                valueFontSize={12}
                backgroundColor='#F9FBFF'
                theme='blue'
                animation={!isPdf}
                resizable={!isPdf}
              />
            </div>
          </Col>
          {predictSale ? <Col span={8}>
            <PredictSale overviewData={overView}/>
          </Col> : <></>}
        </Row>
      </div>
    );
  };
  return (
    <div className={styles.evaluation}>
      {/* 四维一种布局，五维六维另一种布局 */}
      {showTotalScore
        ? (isFiveDimen || isSixDimen ? renderFiveSixDimen() : renderFourDimen())
        : (isFiveDimen || isSixDimen ? renderFiveSixNoSumDimen() : renderFourNoSumDimen())}
      {/* 图片展示区域 */}
      {showPicList ? <ImageLine values={picList} /> : ''}
    </div>
  );
};

export default Evaluation;
